import { AxiosError } from "axios";
import CheckboxFormField from "components/Forms/CheckboxFormField";
import FormField from "components/Forms/FormField";
import PasswordFormField from "components/Forms/PasswordFormField";
import { ChangeEvent, FormEvent, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { CAPTCHA_ACTION_REGISTER } from "../../constants/userConstants";
import useScript from "../../hooks/useScript";
import { IRegisterUser } from "../../interfaces/IRegisterUser";
import { IServiceResponse } from "../../interfaces/IServiceReponse";
import UserService from "../../services/UserService";
import {
  registerUserError,
  registerUserStarted,
  registerUserSuccess,
} from "../../state/user/userActions";
import styles from "./RegisterComponent.module.scss";

// Define types for better code readability
interface RegisterFormState {
  firstname: string;
  surname: string;
  email: string;
  password: string;
  confirmPassword: string;
  consent: boolean;
  reCaptchaToken: string;
}

interface FormError {
  field: string;
  error: string;
}

const RegisterComponent = () => {
  // Initial form state with explicit typing
  const [formState, setFormState] = useState<RegisterFormState>({
    firstname: "",
    surname: "",
    email: "",
    password: "",
    confirmPassword: "",
    consent: false,
    reCaptchaToken: "",
  });

  const [errors, setErrors] = useState<FormError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Generic handler for input changes - reduces code duplication
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormState({
      ...formState,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleRegisterSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await executeReCaptcha();
      await registerUser({
        ...formState,
        reCaptchaToken: token,
      });
    } catch (error) {
      console.error("Registration error:", error);
      setErrors([
        {
          field: "general",
          error: "An unexpected error occurred. Please try again.",
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const executeReCaptcha = (): Promise<string> => {
    return new Promise((resolve) => {
      grecaptcha.ready(function () {
        grecaptcha
          .execute(process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY!, {
            action: CAPTCHA_ACTION_REGISTER,
          })
          .then(resolve);
      });
    });
  };

  const registerUser = async (user: IRegisterUser) => {
    setErrors([]);
    await dispatch(registerUserStarted());

    try {
      const service = new UserService();
      const response = await service.registerUser(user);
      const serviceResponse: IServiceResponse = response.data;

      if (serviceResponse.success) {
        await dispatch(registerUserSuccess());
        navigate("/user/register/success");
      } else {
        mapErrorToField(serviceResponse);
        await dispatch(registerUserError(new Error("Registration failed")));
      }
    } catch (err) {
      const axiosErr = err as AxiosError;
      const serviceResponse = axiosErr.response?.data as IServiceResponse;
      mapErrorToField(serviceResponse);
      await dispatch(registerUserError(err));
    }
  };

  /**
   * Validates all form fields before submission
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = (): boolean => {
    const validationErrors: FormError[] = [];

    // First name validation
    if (!formState.firstname.trim()) {
      validationErrors.push({
        field: "firstname",
        error: "first name is required",
      });
    }

    // Surname validation
    if (!formState.surname.trim()) {
      validationErrors.push({
        field: "lastname",
        error: "surname is required",
      });
    }

    // Email validation
    if (!formState.email.trim()) {
      validationErrors.push({
        field: "email",
        error: "email address is required",
      });
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formState.email)) {
        validationErrors.push({
          field: "email",
          error: "please enter a valid email address",
        });
      }
    }

    // Password validation
    if (!formState.password) {
      validationErrors.push({
        field: "password",
        error: "password is required",
      });
    } else if (formState.password.length < 8) {
      validationErrors.push({
        field: "password",
        error: "your password must be at least 8 characters long",
      });
    }

    // Password confirmation validation
    if (formState.password !== formState.confirmPassword) {
      validationErrors.push({
        field: "confirmPassword",
        error: "the password and confirmation password do not match",
      });
    }

    // Consent validation
    if (!formState.consent) {
      validationErrors.push({
        field: "general",
        error: "you can only register by agreeing to data storage",
      });
    }

    // Update error state if there are any errors
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return false;
    }

    return true;
  };

  const mapErrorToField = (response: IServiceResponse) => {
    if (response?.errors?.length > 0) {
      const mappedErrors: FormError[] = response.errors.map((e) => ({
        field: e.field.toLowerCase(),
        error: e.error.toLowerCase(),
      }));
      setErrors(mappedErrors);
    } else {
      setErrors([
        {
          field: "general",
          error: "Could not create an account with the provided credentials.",
        },
      ]);
    }
  };

  // Helper functions for field errors
  const getFieldError = (fieldName: string): string | undefined => {
    const error = errors.find((error) => error.field === fieldName);
    return error?.error;
  };

  const hasError = (fieldName: string): boolean => {
    return errors.some((error) => error.field === fieldName);
  };

  // Load Google reCAPTCHA script
  const reCaptchaSiteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY!;
  const reCaptchaScriptUrl = `https://www.google.com/recaptcha/api.js?render=${reCaptchaSiteKey}`;
  useScript("reCaptchaScriptContainer", reCaptchaScriptUrl, false);

  return (
    <>
      <div id="reCaptchaScriptContainer" aria-hidden="true"></div>
      <div className="container-fluid p-10">
        <div className="account-pages">
          <div className="flex flex-col gap-2 ">
            <div className="col-12 md:max-w-4xl">
              <form
                onSubmit={handleRegisterSubmit}
                noValidate={true}
                aria-label="Registration form"
              >
                <h1 className="registerHeading text-heading-1 ml-0">
                  Get Started
                </h1>

                <input
                  name="__RequestVerificationToken"
                  type="hidden"
                  defaultValue="T-sGOiAfRe9GfY1hlGaIXQ3cA_8SVSFYq03VkrLiu9u6aqP3ly0TNucgpRZD5q94LbW4Sj6Mny6Ats-WFC3xI7q1dKlV-YxM4kzVvDrQaHI1"
                />

                <div className="flex flex-col gap-4">
                  <FormField
                    label="First Name"
                    name="firstname" // Changed to match state property
                    error={hasError("firstname")}
                    errorMessage={getFieldError("firstname")}
                    validated={hasError("firstname")}
                    onChange={handleInputChange}
                    className={styles.formGroup}
                    value={formState.firstname}
                    disabled={isSubmitting}
                    autoComplete="given-name"
                  />

                  <FormField
                    label="Surname"
                    name="surname"
                    error={hasError("lastname")}
                    errorMessage={getFieldError("lastname")}
                    validated={hasError("lastname")}
                    onChange={handleInputChange}
                    className={styles.formGroup}
                    value={formState.surname}
                    disabled={isSubmitting}
                    autoComplete="family-name"
                  />

                  <FormField
                    label="Email Address"
                    name="email"
                    error={hasError("email")}
                    errorMessage={getFieldError("email")}
                    validated={hasError("email")}
                    onChange={handleInputChange}
                    className={styles.formGroup}
                    value={formState.email}
                    disabled={isSubmitting}
                    autoComplete="email"
                    type="email"
                  />

                  <PasswordFormField
                    label="Password"
                    name="password"
                    error={hasError("password")}
                    errorMessage={getFieldError("password")}
                    validated={hasError("password")}
                    onChange={handleInputChange}
                    className={styles.formGroup}
                    value={formState.password}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />

                  <PasswordFormField
                    label="Confirm Password"
                    name="confirmPassword"
                    error={hasError("confirmPassword")}
                    errorMessage={getFieldError("confirmPassword")}
                    validated={hasError("confirmPassword")}
                    onChange={handleInputChange}
                    className={styles.formGroup}
                    value={formState.confirmPassword}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />

                  <p className="text-label-2">
                    Consent for storing submitted data.
                  </p>

                  <CheckboxFormField
                    label="Yes, I give permission to store and process my data"
                    name="consent"
                    error={hasError("general")}
                    errorMessage={getFieldError("general")}
                    validated={hasError("general")}
                    onChange={handleInputChange}
                    className={styles.formGroup}
                    disabled={isSubmitting}
                    checked={formState.consent}
                  />

                  <p className="text-label-3">
                    <span>Learn more about how we store and use data: </span>
                    <a
                      className="text-underline text-black"
                      href="https://www.mim.ac/privacy-policy/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Click here to view our Privacy Policy
                    </a>
                  </p>
                </div>

                <input
                  id="SubscriptionId"
                  name="SubscriptionId"
                  type="hidden"
                  defaultValue=""
                />
                <input
                  autoComplete="off"
                  id="SecondaryEmail"
                  name="SecondaryEmail"
                  type="hidden"
                  defaultValue=""
                />

                <div className="form-group submit-btn mt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="primary-3"
                    className="w-100"
                    aria-label={isSubmitting ? "Registering..." : "Register"}
                  >
                    {isSubmitting ? (
                      <div className="register_loading">
                        <div
                          className="loader rotate mt-3"
                          aria-hidden="true"
                        ></div>
                        <span className="sr-only">Registering...</span>
                      </div>
                    ) : (
                      "Register"
                    )}
                  </Button>
                </div>
              </form>

              <div className="registerAccount mt-4">
                <p className="text-center">
                  Already have an account?{" "}
                  <a
                    href="/"
                    className="ms-2 fw-medium"
                    data-variant="secondary"
                  >
                    Sign In &gt;
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterComponent;
